import React from 'react';
import { Container, Typography, Box, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const ManagingVideos: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/help')}
          sx={{ mb: 4, color: 'text.secondary' }}
        >
          {t('help.backToHelp', 'Back to Help Center')}
        </Button>

        <Typography variant="overline" color="primary" sx={{ fontWeight: 600, letterSpacing: 2, display: 'block', mb: 2 }}>
          FOR CREATORS
        </Typography>

        <Typography variant="h3" component="h1" sx={{ mb: 3, fontWeight: 700 }}>
          Deleting or Editing Your Videos
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.85, fontSize: '1.1rem' }}>
          Managing your video content is an important part of maintaining a successful channel on IndeeClips. Whether you need to update video details, replace outdated content, or remove videos entirely, our platform provides tools to help you keep your channel current and professional.
        </Typography>

        <Typography variant="h5" sx={{ mt: 5, mb: 2, fontWeight: 700 }}>
          How to Edit Video Details
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
          You can update video information without re-uploading the entire video file. This is useful for fixing typos, updating descriptions, or changing thumbnails.
        </Typography>
        <Box component="ol" sx={{ pl: 3, mb: 4 }}>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            Navigate to your channel page and locate the video you want to edit
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            Click on the video settings or edit icon (usually three dots or a pencil icon)
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            Update the title, description, tags, or thumbnail as needed
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            Click "Save Changes" to update the video information
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ lineHeight: 1.85 }}>
            Changes typically appear immediately, though search indexing may take a few hours
          </Typography>
        </Box>

        <Typography variant="h5" sx={{ mt: 5, mb: 2, fontWeight: 700 }}>
          What You Can Edit
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 4 }}>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            <strong>Video title:</strong> Update to improve clarity or SEO without affecting the video file itself
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            <strong>Description:</strong> Add timestamps, correct information, or update links and resources
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            <strong>Thumbnail:</strong> Replace with a more eye-catching or relevant image to improve click-through rates
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            <strong>Tags and categories:</strong> Optimize for better discoverability in search and recommendations
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ lineHeight: 1.85 }}>
            <strong>Privacy settings:</strong> Change video visibility (if applicable to your account type)
          </Typography>
        </Box>

        <Typography variant="h5" sx={{ mt: 5, mb: 2, fontWeight: 700 }}>
          How to Delete a Video
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
          Deleting a video permanently removes it from IndeeClips. This action cannot be undone, so make sure you truly want to remove the content before proceeding.
        </Typography>
        <Box component="ol" sx={{ pl: 3, mb: 4 }}>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            Go to your channel page and find the video you want to delete
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            Click on the video settings menu (three dots or edit icon)
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            Select "Delete Video" from the options menu
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            Confirm the deletion when prompted (you may need to confirm twice)
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ lineHeight: 1.85 }}>
            The video will be removed from your channel and all views, likes, and comments will be permanently lost
          </Typography>
        </Box>

        <Typography variant="h5" sx={{ mt: 5, mb: 2, fontWeight: 700 }}>
          Before Deleting: Consider These Alternatives
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 4 }}>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            <strong>Make it private:</strong> If available, change the video to private instead of deleting to preserve analytics and history
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            <strong>Update instead of delete:</strong> For outdated information, consider editing the description to add corrections rather than removing the video
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            <strong>Replace the thumbnail:</strong> A poor-performing video might just need a better thumbnail to attract viewers
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ lineHeight: 1.85 }}>
            <strong>Archive the URL:</strong> If you have the video embedded elsewhere or shared publicly, note that those links will break after deletion
          </Typography>
        </Box>

        <Typography variant="h5" sx={{ mt: 5, mb: 2, fontWeight: 700 }}>
          Important Notes About Deletion
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 4 }}>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            Deletion is permanent and cannot be reversed - download a copy of your video before deleting if you might want it later
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            All engagement metrics (views, likes, comments) associated with the video will be lost
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            The video URL will become inactive, and anyone trying to access it will see an error
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ lineHeight: 1.85 }}>
            If the video violated platform policies, deleting it does not necessarily remove strikes or warnings from your account
          </Typography>
        </Box>

        <Box
          sx={{
            mt: 6,
            p: 4,
            bgcolor: 'background.paper',
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
            Need Help Managing Your Videos?
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.85 }}>
            If you're experiencing issues editing or deleting videos, or have questions about managing your content library, please contact our support team. Email{' '}
            <Typography
              component="a"
              href="mailto:contact@indeeclips.com"
              sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              contact@indeeclips.com
            </Typography>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default ManagingVideos;
